/** @file Field - Controller for a single field. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { helpers } from '@codegrade/utils';
import * as utils from '@/utils';
import * as constants from '../constants';
import { FieldContextKey } from '../context';
import { Description } from './Description';
import { Label } from './Label';
import { ValidationMessage } from './ValidationMessage';
/** Field component generates unique IDs and make them available to the childrens without having you to explicitly passing them. */
export const Field = tsx
    .componentFactoryOf()
    .create({
    name: constants.nameField,
    props: {
        label: p.ofType().optional,
        description: p.ofType().optional,
        validation: p.ofType().optional,
    },
    /** @returns Internal state of the component. */
    data() {
        const uniqueId = utils.getUniqueId();
        return {
            uniqueId,
            getDescriptionId: helpers.makeUniqueCounter(),
            getValidationMessageId: helpers.makeUniqueCounter(),
            registeredElementIds: [],
        };
    },
    /** @returns Provided values. */
    provide() {
        return { [FieldContextKey]: () => this.contextData };
    },
    computed: {
        /** @returns The data to provide in the Field context. */
        contextData() {
            const id = this.uniqueId;
            const inputId = `cg-input-${id}`;
            const descriptionId = this.registeredElementIds.length > 0
                ? this.registeredElementIds.join(' ')
                : undefined;
            return {
                inputId,
                descriptionId,
                register: type => {
                    const getId = {
                        validation: this.getValidationMessageId,
                        description: this.getDescriptionId,
                    }[type];
                    const elementId = `${inputId}-${type}-${getId()}`;
                    this.registeredElementIds.push(elementId);
                    return elementId;
                },
                deregister: idToRemove => {
                    this.registeredElementIds = this.registeredElementIds.filter(elementId => elementId !== idToRemove);
                },
            };
        },
        /** @returns The Node for the label content. */
        labelNode() {
            if (this.label == null) {
                return utils.emptyVNode();
            }
            return <Label>{this.label}</Label>;
        },
        /** @returns The Node for the description content. */
        descriptionNode() {
            return utils.ifOrEmpty(this.description != null, () => (<Description>{this.description}</Description>));
        },
    },
    /** @returns Nodes. */
    render() {
        return (<div data-cy={constants.nameField} class={[constants.nameField]}>
                    {this.labelNode}
                    {utils.renderScopedSlot(this.contextData, this.$scopedSlots.default, utils.emptyVNode)}
                    {utils.emptyIfNil(this.validation, msg => (<ValidationMessage message={msg}/>))}
                    {this.descriptionNode}
                </div>);
    },
});
